/**
 * ndev-cart-state.js · Black Friday V2 · 北美购物车抽屉状态层
 *
 * 对外契约:window.ndevCart = { refresh, applyCode, removeCode, changeLine, version }
 *   refresh({source, open})        -> Promise<cart|null>  返回最终确认Cart；排期重算失败返回null(open默认true)
 *   applyCode(code)                -> Promise<{status}>  status: applied|not_applied|duplicate|empty|limit|error|unconfirmed
 *     limit: 最新购物车已保存至少 5 码时拦截新码,不提交 update、不改原车及码,保留草稿;重复码仍走原逻辑。
 *   removeCode(code)               -> Promise<{status}>  status: removed|error|unconfirmed
 *   changeLine(key|{key,index,quantity,groupId,removeSameGroup}, qty) -> Promise<cart>
 * 事件:document 上派发 `ndev:cart:changed`,detail = { cart, source }
 * 结账扩展: `ndev:cart:before-checkout` 的 detail.waitFor(promise) 须在派发期间同步登记；
 *   无登记时保留原生导航。登记后等待全部 settled，最长 3000ms；事件取消时不导航。
 *
 * 设计要点
 *  1. 所有写/读经同一串行队列;同 key 的数量修改在未开始前合并为最后一个目标值;同批次内多次 refresh 合并。
 *  2. 队列清空后才渲染抽屉,且只渲染一次:复用 vendor `$360.showHeaderCart({update:true})`(cart.js + cart?view=json 整体替换
 *     #header-cart 内容,附带 Swiper/高度/Klarna/计数等既有后处理,不重复造轮子)。
 *  3. 渲染后用服务端 cart.js 的 sig 与 DOM 上 data-ndev-cart-sig 对账,不一致最多重渲染 2 次(防「cart.js 与 view 不是同一时刻」)。
 *  4. 折扣码:提交完整期望列表(含此前保留的未生效码),回读 cart.js 判断;Shopify 对「无效/过期」与「有效但不可叠加/不如满减」
 *     都返回 applicable:false,Ajax 无法区分,所以未生效的码保留在列表里显示「Not applied」+ 统一说明,不断言「invalid」;前端不比较择优。
 *  5. 对 $360.showHeaderCart 做运行时去重包装(见文件末尾),解决 vendor updateItemQty 逗号表达式导致的双回调。
 *  6. 绝不展示与服务端不一致的旧金额:vendor 的 sessionStorage `shopify-header-cart` 缓存会在页面加载时被灌进抽屉(ndev-narwal-common),
 *     而 vendor 的 showHeaderCart 在 fetch 之前就先加 active 打开抽屉。所以(a)任何 mutation 入队/执行后立即清掉该缓存;
 *     (b)抽屉内容未被本脚本对账过(sig 与最近一次确认的 cart 不一致)或抽屉原本是关的,渲染期间盖住旧内容并显示 loading,渲染完成才露出;
 *     (c)非 update 的「仅打开」调用,若 DOM 未对账,改走 refresh 先取最新 cart。
 *  ⚠ 第三方 Quantity Breaks Now 会按通用选择器接管价格 DOM,对本抽屉的实际影响未验证(待预览实测)。
 */
(function () {
  'use strict';
  if (window.ndevCart && window.ndevCart.version) return;

  var T = window.ndevCartI18n || {};
  var REQ_TIMEOUT = 15000;
  var RENDER_TIMEOUT = 12000;

  // Shopify 的 t 过滤器会 HTML 转义(Spend & Save -> Spend &amp; Save);这些串只经 textContent/setAttribute 使用,所以先解码,
  // 否则页面会显示字面 `&amp;`(第 2 轮真店实测)。
  function decodeEntities(s) {
    return s.replace(/&(amp|lt|gt|quot|#39|#x27);/g, function (m, e) {
      return { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", '#x27': "'" }[e];
    });
  }
  function t(key, vars) {
    var s = T[key];
    if (typeof s !== 'string') return '';
    s = decodeEntities(s);
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), vars[k]);
      });
    }
    return s;
  }
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function lc(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  function sig(cart) {
    return [cart.item_count, cart.original_total_price, cart.total_discount, cart.total_price].join(':');
  }

  /* ---------------- network ---------------- */
  function request(path, init) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, REQ_TIMEOUT) : null;
    init = init || {};
    init.credentials = 'same-origin';
    init.headers = Object.assign({ Accept: 'application/json' }, init.headers || {});
    if (ctrl) init.signal = ctrl.signal;
    return fetch(root() + path, init).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = txt ? JSON.parse(txt) : null; } catch (e) { data = null; }
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || ('HTTP ' + res.status));
          err.status = res.status;
          err.serverMessage = data && typeof (data.description || data.message) === 'string' ? (data.description || data.message) : '';
          throw err;
        }
        return data;
      });
    }).then(function (d) { if (timer) clearTimeout(timer); return d; },
            function (e) { if (timer) clearTimeout(timer); throw e; });
  }
  function postJSON(path, body) {
    return request(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  }

  /* ---------------- state ---------------- */
  var state = { cart: null, seq: 0 };
  // An elapsed display window also needs a native pricing recalculation:
  // Cart GETs alone can retain a scheduled automatic discount's previous state.
  // The empty updates map changes no quantities, codes, properties or plans.
  var activityRepriceKey = null;
  var activityPricingConfirmedKey = '';
  var pricingEpoch = 0;
  var visibilityEpoch = 0;
  var visibilityWantedOpen = true;
  function noteVisibility(open) { visibilityWantedOpen = open; visibilityEpoch++; }
  var seqCounter = 0;
  function nextSeq() { return ++seqCounter; }
  // 旧响应不得覆盖新状态:只接受 seq 不小于当前已接受的响应
  function accept(cart, seq) {
    if (cart && seq >= state.seq) { state.cart = cart; state.seq = seq; return true; }
    return false;
  }
  function usableCart(cart) {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart) || !Array.isArray(cart.items) || !Array.isArray(cart.discount_codes)) return false;
    var amounts = ['item_count', 'original_total_price', 'total_discount', 'total_price'];
    if (!amounts.every(function (key) { return Number.isFinite(cart[key]) && cart[key] >= 0; })) return false;
    return cart.discount_codes.every(function (entry) {
      return entry && typeof entry.code === 'string' && entry.code.trim() &&
        (entry.applicable === undefined || typeof entry.applicable === 'boolean');
    });
  }
  function readCart() {
    var seq = nextSeq();
    requireActivityPricing(activityWindow());
    var repriceKey = activityRepriceKey;
    var response = repriceKey ? postJSON('cart/update.js', { updates: {} }) : request('cart.js?_=' + Date.now());
    return response.then(function (c) {
      // Never write a guessed empty code list after an incomplete Cart response.
      if (!usableCart(c)) throw new Error('Unconfirmed Cart response');
      if (repriceKey) {
        activityPricingConfirmedKey = repriceKey;
        if (activityRepriceKey === repriceKey) activityRepriceKey = null;
      }
      accept(c, seq);
      return c;
    });
  }

  /* ---------------- 折扣码 helpers ---------------- */
  function codeEntries(cart) { return (cart && cart.discount_codes) || []; }
  function confirmedCodes(cart) {
    return codeEntries(cart).filter(function (e) { return e.applicable !== false; }).map(function (e) { return e.code; });
  }
  // 购物车里保留的全部码(含 applicable:false:Shopify 会保留,条件变化时可能自动生效)
  function listedCodes(cart) { return codeEntries(cart).map(function (e) { return e.code; }); }
  function unappliedCodes(cart) {
    return codeEntries(cart).filter(function (e) { return e.applicable === false; }).map(function (e) { return e.code; });
  }
  function hasCode(list, code) { return list.some(function (c) { return lc(c) === lc(code); }); }
  // 码是否出现在服务端实际分摊里(cart 级 / 行级)
  function codeAdopted(cart, code) {
    var c = lc(code);
    var apps = (cart.cart_level_discount_applications || []);
    if (apps.some(function (a) { return a.type === 'discount_code' && lc(a.title) === c; })) return true;
    return (cart.items || []).some(function (it) {
      return (it.line_level_discount_allocations || []).some(function (al) {
        var a = al.discount_application || {};
        return a.type === 'discount_code' && lc(a.title) === c;
      });
    });
  }
  // accepted | not_applied(applicable:false 或未被保留:原因无法从 Ajax 区分,不当作 invalid)
  function classify(cart, code) {
    var entry = codeEntries(cart).filter(function (e) { return lc(e.code) === lc(code); })[0];
    if (entry && entry.applicable === true) return codeAdopted(cart, code) ? 'accepted' : 'not_applied';
    if (entry && entry.applicable === undefined) return codeAdopted(cart, code) ? 'accepted' : 'not_applied';
    return 'not_applied';
  }
  function normalizeCode(raw) { return String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim(); }

  /* ---------------- 缓存 / 对账 / 旧内容遮罩 ---------------- */
  var verifiedSig = null;   // 最近一次「DOM 的 sig == 服务端 cart sig」的确认值
  function clearCache() { try { sessionStorage.removeItem('shopify-header-cart'); } catch (e) {} }
  function domSig() { var f = $('[data-ndev-cart-sig]'); return f ? f.getAttribute('data-ndev-cart-sig') : null; }
  function domVerified() { var s = domSig(); return !activityRepriceKey && !!s && s === verifiedSig; }
  function markVerified(n) { if (n && domSig() === sig(n)) verifiedSig = sig(n); armActivityWindow(); }
  function maskStale(on) {
    var d = document.getElementById('header-cart');
    if (!d) return;
    var el = d.querySelector(':scope > .ndev-cart-loading');
    if (on) {
      d.classList.add('is-ndev-stale');
      if (!el) {
        el = document.createElement('div');
        el.className = 'ndev-cart-loading';
        el.setAttribute('role', 'status');
        el.textContent = t('promo_updating');
        d.insertBefore(el, d.firstChild);
      }
    } else {
      d.classList.remove('is-ndev-stale');
      if (el) el.remove();
    }
  }

  /* ---------------- UI: busy / live / message ---------------- */
  var ui = { msg: null, lineError: '', clearDraft: false };
  var liveEl = null;
  function clearVisibleError() {
    if (!liveEl) return;
    liveEl.classList.remove('is-visible', 'ndev-cart-promo__msg');
    liveEl.style.removeProperty('top');
  }
  function announce(text, visibleError) {
    if (!text) return;
    if (!liveEl) {
      liveEl = document.createElement('div');
      liveEl.className = 'ndev-cart-live';
      liveEl.setAttribute('role', 'status');
      liveEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveEl);
    }
    clearVisibleError();
    if (visibleError) {
      // A failed cold open cannot expose the old Cart. Keep its ordinary error
      // outside the hidden/inert drawer, without locking the page or focus.
      liveEl.classList.add('is-visible', 'ndev-cart-promo__msg');
      var header = document.querySelector('ndev-site-header');
      var bottom = header ? header.getBoundingClientRect().bottom : 0;
      liveEl.style.top = Math.max(12, Math.min(window.innerHeight - 96, bottom + 12)) + 'px';
    }
    liveEl.textContent = '';
    setTimeout(function () { liveEl.textContent = text; }, 30);
  }
  function drawer() { return document.getElementById('header-cart'); }
  // 点击立即反馈:数量或商品行先变,金额淡出到服务器确认;结果以随后的整体渲染为准,失败时也由它恢复。
  function showLineChange(args) {
    var d = drawer();
    if (!d) return;
    d.classList.add('is-ndev-pricing');
    var input = $$('.global-qty-text', d).filter(function (el) {
      var call = el.getAttribute('oninput') || '';
      return args.key ? call.indexOf(args.key) !== -1 : call.indexOf('index: ' + args.index + ',') !== -1;
    })[0];
    if (!input) return;
    input.value = args.quantity;
    if (args.quantity === 0) {
      var row = input.closest('li');
      if (row) row.classList.add('is-ndev-removing');
    }
  }
  function setBusy(on) {
    var d = drawer();
    if (d) {
      d.classList.toggle('is-ndev-busy', !!on);
      if (!on) d.classList.remove('is-ndev-pricing');
      if (on) d.setAttribute('aria-busy', 'true'); else d.removeAttribute('aria-busy');
    }
    $$('[data-ndev-promo-apply]').forEach(function (b) { b.disabled = !!on; });
    var p = $('[data-ndev-promo]');
    if (p) p.classList.toggle('is-busy', !!on);
  }
  function applyMessage() {
    var el = $('[data-ndev-promo-msg]');
    var input = $('[data-ndev-promo-input]');
    if (el) {
      if (ui.msg && ui.msg.text) {
        el.textContent = ui.msg.text + (ui.msg.ssHint && ssApplied() ? ' ' + t('promo_ss_better') : '');
        el.hidden = false;
        el.classList.toggle('is-info', ui.msg.kind === 'info' || ui.msg.kind === 'invalid');
        el.classList.remove('is-invalid');
      } else {
        el.textContent = ui.codeNoticeText || '';
        el.hidden = !ui.codeNoticeText;
        el.classList.toggle('is-info', !!ui.codeNoticeText);
        el.classList.remove('is-invalid');
      }
    }
    if (input) {
      if (ui.msg && (ui.msg.kind === 'error' || ui.msg.kind === 'invalid')) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
    // 行级/通用错误:优先放在整个购物车滚动面首部(role=alert);没有列表时只走 live region
    $$('.ndev-cart-error').forEach(function (n) { n.remove(); });
    if (ui.lineError) {
      var list = $('#header-cart .header-cart-items') || $('.header-cart-items');
      if (list && list.parentNode) {
        var p = document.createElement('p');
        p.className = 'ndev-cart-error ndev-cart-promo__msg';
        p.setAttribute('role', 'alert');
        p.textContent = ui.lineError;
        var errorForm = list.closest('form');
        if (errorForm) errorForm.insertBefore(p, errorForm.firstChild);
        else list.parentNode.insertBefore(p, list);
      }
    }
  }
  function setMsg(kind, text, ssHint) { ui.msg = text ? { kind: kind, text: text, ssHint: !!ssHint } : null; }
  // 平台无法区分「无效/过期」与「有效但不可叠加」:首次提示与刷新后的通用说明保持一致(中性文案,不断言 invalid)
  function setInvalid(code) { setMsg('invalid', t('promo_not_applied_msg'), true); if (ui.msg) ui.msg.code = code; }

  /* ---------------- UI: 保留焦点 / 输入 / 滚动 ---------------- */
  function captureUI() {
    var snap = { draft: null, selStart: null, selEnd: null, focus: null, scrolls: [] };
    var input = $('[data-ndev-promo-input]');
    if (input) { snap.draft = input.value; try { snap.selStart = input.selectionStart; snap.selEnd = input.selectionEnd; } catch (e) {} }
    var ae = document.activeElement;
    var d = drawer();
    if (ae && d && d.contains(ae)) {
      if (ae.matches('[data-ndev-promo-input]')) snap.focus = { kind: 'input' };
      else if (ae.matches('[data-ndev-promo-apply]')) snap.focus = { kind: 'apply' };
      else if (ae.hasAttribute('data-ndev-remove-code')) snap.focus = { kind: 'remove-code' };
      else if (ae.matches('.checkout-btn')) snap.focus = { kind: 'checkout' };
      else if (ae.matches('.header-cart-close')) snap.focus = { kind: 'close' };
      else {
        var li = ae.closest('.header-cart-items > li');
        if (li) {
          var items = $$('.header-cart-items > li', d);
          var cls = ['global-plus', 'global-minus', 'global-qty-text', 'button-trash'].filter(function (c) { return ae.classList.contains(c); })[0];
          snap.focus = { kind: 'line', index: items.indexOf(li), cls: cls || null };
        }
      }
      if (snap.focus) {
        var focusBox = ae.getBoundingClientRect(), focusForm = ae.closest('form');
        var focusViewport = focusForm && focusForm.getBoundingClientRect();
        if (focusViewport && focusBox.top >= focusViewport.top && focusBox.bottom <= focusViewport.bottom) snap.focus.top = focusBox.top;
      }
    }
    if (d) {
      // The error/loading nodes change descendant ordinals across a refresh.
      // The v2 form owns scrolling; restore that owner rather than an old index.
      snap.scrolls.push({ kind: 'drawer', top: d.scrollTop });
      var form = d.querySelector('[data-ndev-drawer] > form');
      if (form) snap.scrolls.push({ kind: 'form', top: form.scrollTop });
    }
    return snap;
  }
  function restoreUI(snap) {
    var d = drawer();
    if (!d || !snap) return;
    var input = $('[data-ndev-promo-input]');
    if (input && snap.draft != null && !ui.clearDraft) {
      input.value = snap.draft;
    }
    ui.clearDraft = false;
    snap.scrolls.forEach(function (s) {
      var owner = s.kind === 'drawer' ? d : d.querySelector('[data-ndev-drawer] > form');
      if (owner) owner.scrollTop = s.top;
    });
    var f = snap.focus, target = null;
    if (f) {
      if (f.kind === 'input' || f.kind === 'remove-code') target = input;
      else if (f.kind === 'apply') target = $('[data-ndev-promo-apply]');
      else if (f.kind === 'checkout') target = d.querySelector('.checkout-btn');
      else if (f.kind === 'close') target = d.querySelector('.header-cart-close');
      else if (f.kind === 'line') {
        var li = $$('.header-cart-items > li', d)[f.index];
        if (li) target = (f.cls && li.querySelector('.' + f.cls)) || li.querySelector('.global-qty-text, button');
      }
    }
    if (target && typeof target.focus === 'function') {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      // Content height can change even when the same scroll offset is restored.
      // Keep an actual restored focus visible without scrolling ordinary reads.
      var scroller = target.closest('form');
      if (document.activeElement === target && scroller) {
        var box = target.getBoundingClientRect(), viewport = scroller.getBoundingClientRect();
        if (f.top != null) {
          scroller.scrollTop += box.top - f.top;
          box = target.getBoundingClientRect();
        }
        // The 2px focus ring plus its 2px offset must also stay inside.
        if (box.top < viewport.top + 4) scroller.scrollTop -= viewport.top + 4 - box.top;
        else if (box.bottom > viewport.bottom - 4) scroller.scrollTop += box.bottom - viewport.bottom + 4;
      }
      if (f.kind === 'input' && snap.selStart != null) { try { target.setSelectionRange(snap.selStart, snap.selEnd); } catch (e) {} }
    }
  }

  /* ---------------- UI: chips 补全(服务端只渲染「被采用」的码) ---------------- */
  function ssApplied() {
    var ss = $('[data-ndev-ss]');
    return !!(ss && (Number(ss.getAttribute('data-ss-total')) > 0 || Number(ss.getAttribute('data-tier-server')) > 0));
  }
  function makeChip(code) {
    var li = document.createElement('li');
    li.className = 'ndev-cart-promo__chip';
    li.setAttribute('data-code', code);
    var main = document.createElement('span');
    main.className = 'ndev-cart-promo__chip-main';
    var cs = document.createElement('span'); cs.className = 'ndev-cart-promo__code'; cs.textContent = code;
    var st = document.createElement('span'); st.className = 'ndev-cart-promo__status';
    main.appendChild(cs); main.appendChild(document.createTextNode(' ')); main.appendChild(st);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ndev-cart-promo__remove';
    btn.setAttribute('data-ndev-remove-code', code);
    btn.setAttribute('aria-label', t('promo_remove_aria').replace('__CODE__', code));   // theme.liquid 以 __CODE__ 占位输出,避免 locale 变量缺失
    btn.textContent = t('promo_remove');
    li.appendChild(main); li.appendChild(btn);
    return li;
  }
  function decorate(cart) {
    ui.codeNoticeText = '';
    var ul = $('[data-ndev-chips]');
    if (!ul || !cart) return;
    var ss = ssApplied();
    var notApplied = false, notAdopted = false;
    $$('.ndev-cart-promo__note', ul).forEach(function (node) { node.remove(); });
    var chipFor = function (code) {
      return $$('[data-code]', ul).filter(function (n) { return lc(n.getAttribute('data-code')) === lc(code); })[0];
    };
    codeEntries(cart).forEach(function (e) {
      var code = e.code;
      var li = chipFor(code);
      if (e.applicable === false) {
        // 有效性无法判断(过期/不适用/不可叠加/不如满减都是 applicable:false):保留并诚实说明
        if (!li) { li = makeChip(code); ul.appendChild(li); }
        li.setAttribute('data-status', 'not-applied');
        var st = li.querySelector('.ndev-cart-promo__status');
        if (st) st.textContent = '· ' + t('promo_not_applied');
        notApplied = true;
        return;
      }
      var adopted = codeAdopted(cart, code);
      if (!li) {
        li = makeChip(code);
        li.querySelector('.ndev-cart-promo__status').textContent = '· ' + t('promo_applied');
        ul.appendChild(li);
      }
      // 被识别不等于已减免；状态只依据本次实际分摊，不在前端比较优惠。
      li.setAttribute('data-status', adopted ? 'applied' : 'not-adopted');
      li.querySelector('.ndev-cart-promo__status').textContent = '· ' + t(adopted ? 'promo_applied' : 'promo_not_applied');
      if (!adopted) {
        li.setAttribute('data-status', 'not-adopted');
        if (ss) notAdopted = true; else notApplied = true;
      }
    });
    // 失效码提示只跟随对应码:码已移除或资格变化后被采用,提示随回读消失
    if (ui.msg && ui.msg.kind === 'invalid') {
      var stillOff = codeEntries(cart).some(function (e) { return lc(e.code) === lc(ui.msg.code) && (e.applicable === false || !codeAdopted(cart, e.code)); });
      if (!stillOff) ui.msg = null;
    }
    if (notApplied) ui.codeNoticeText = t('promo_not_applied_msg') + (ss ? ' ' + t('promo_ss_better') : '');
    else if (notAdopted) ui.codeNoticeText = t('promo_not_combinable');
    else if (ss && listedCodes(cart).some(function (code) { return codeAdopted(cart, code); })) {
      // 成功说明由本次SSR账本给出实际金额；不在客户端重算优惠或沿用上次金额。
      var msg = $('[data-ndev-promo-msg]');
      ui.codeNoticeText = msg ? msg.getAttribute('data-combined-message') || '' : '';
    }
  }

  /* ---------------- 变更:折扣码 ---------------- */
  function submitCodes(list) { return postJSON('cart/update.js', { discount: list.join(',') }); }

  // 提交 base 列表并回读,确认服务端列表包含 base 的全部码;返回回读 cart 或 null(无法确认)
  function restoreCodes(base) {
    return submitCodes(base).catch(function () { /* 结果不明,以回读为准 */ }).then(readCart).then(function (c) {
      var cur = listedCodes(c);
      return base.every(function (b) { return hasCode(cur, b); }) ? c : null;
    }, function () { return null; });
  }

  function doApply(raw) {
    var code = normalizeCode(raw);
    if (!code) { setMsg('error', t('promo_empty')); return Promise.resolve({ status: 'empty' }); }
    if (code.indexOf(',') !== -1) { setMsg('error', t('promo_not_applied_msg')); return Promise.resolve({ status: 'not_applied' }); }
    return readCart().then(function (c0) {
      // base = 购物车里保留的全部码(含此前未生效的),提交时一并带上,不丢
      var base = listedCodes(c0);
      if (hasCode(base, code)) {
        // 已在列表:已生效 = 重复;未生效 = 不重复提交,再给一次统一说明
        var again = classify(c0, code);
        if (again === 'accepted') { setMsg('info', ''); ui.clearDraft = true; return { status: 'duplicate' }; }
        // 失效码提示(设计 7000:1512):保留输入,红色提示;chip 状态不变
        ui.clearDraft = false;
        setInvalid(code);
        return { status: 'not_applied' };
      }
      // Shopify 超量提交可能返回 200 却不采用有效码;全部保存码(含 applicable:false)都计入上限。
      // 必须在最新回读和重复码判断后拦截,不删码、不比较择优、不发 update/恢复请求。
      if (base.length >= 5) {
        ui.clearDraft = false;
        setMsg('info', t('promo_limit'));
        return { status: 'limit' };
      }
      var want = base.concat(code);
      var postErr = null;
      return submitCodes(want).catch(function (e) { postErr = e; }).then(readCart).then(function (c1) {
        var cls = classify(c1, code);
        var cur = listedCodes(c1);
        var lost = base.some(function (b) { return !hasCode(cur, b); });
        var done = function (restoredOk) {
          if (!restoredOk) { setMsg('error', t('promo_unconfirmed')); return { status: 'unconfirmed' }; }
          if (cls === 'accepted') { setMsg('info', ''); ui.clearDraft = true; return { status: 'applied' }; }
          if (postErr && !postErr.status) { setMsg('error', t('promo_error')); return { status: 'error' }; }
          // 未生效:码留在列表(chip 显示 Not applied),原有码不动;本次提交给失效码提示并保留输入(设计 7000:1512)
          ui.clearDraft = false;
          if (lost) setMsg('info', t('promo_restored')); else setInvalid(code);
          return { status: 'not_applied' };
        };
        if (!lost) return done(true);
        return restoreCodes(base).then(function (rc) { return done(!!rc); });
      }, function () {
        setMsg('error', t('promo_unconfirmed'));
        return { status: 'unconfirmed' };
      });
    }, function () { setMsg('error', t('promo_error')); return { status: 'error' }; });
  }

  function doRemove(raw) {
    var code = normalizeCode(raw);
    return readCart().then(function (c0) {
      var rest = listedCodes(c0).filter(function (c) { return lc(c) !== lc(code); });
      var postErr = null;
      return submitCodes(rest).catch(function (e) { postErr = e; }).then(readCart).then(function (c1) {
        if (hasCode(listedCodes(c1), code)) { setMsg('error', t('promo_error')); return { status: 'error' }; }
        setMsg('info', '');
        ui.clearDraft = true;   // 删码成功一并清空输入框(运营 2026-11-01 要求)
        return { status: 'removed' };
      }, function () { setMsg('error', t('promo_unconfirmed')); return { status: 'unconfirmed' }; });
    }, function () { setMsg('error', t('promo_error')); return { status: 'error' }; });
  }

  /* ---------------- 变更:数量 / 删除 ---------------- */
  // 行身份:variant + 行属性 + selling plan。折扣变化会改变 line key(实测:连点 + 时第二次 change.js 用旧 key 返回 400),
  // 所以按身份在「最新已确认的 cart」里重新映射 key;映射不到再退回 DOM 上的 key。
  // 取舍:不用 `line` 序号定位——前序行被删/合并时序号会漂移,会改错行;key 重映射同一行最稳。
  function identOf(item) {
    if (!item) return null;
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan ? item.selling_plan_allocation.selling_plan.id : '';
    return item.variant_id + '|' + JSON.stringify(item.properties || {}) + '|' + plan;
  }
  function itemByKey(cart, key) {
    return ((cart && cart.items) || []).filter(function (it) { return it.key === key; })[0] || null;
  }
  function identFromArgs(a) {
    var c = state.cart;
    if (!c || !c.items) return null;
    var it = a.key ? itemByKey(c, a.key) : c.items[a.index - 1];
    return identOf(it);
  }
  // 以最新 state.cart 解析任务当前应使用的 key;找不到身份对应的行返回 null
  function resolveKey(a) {
    var c = state.cart;
    if (a.ident && c && c.items) {
      var hit = c.items.filter(function (it) { return identOf(it) === a.ident; })[0];
      if (hit) return hit.key;
      return null;
    }
    return a.key || null;
  }
  // 写请求同一响应带回的抽屉 HTML:只给紧随其后的那次渲染用,其它任务一律作废,退回读取。
  var DRAWER_SECTION = 'ndev-cart-drawer-render';
  var drawerHtml = null;
  function keepDrawerHtml(cart) {
    var html = cart && cart.sections && cart.sections[DRAWER_SECTION];
    if (cart) delete cart.sections;
    drawerHtml = html ? { cart: cart, html: html } : null;
    return cart;
  }
  function doChange(a) {
    function attempt(retried) {
      var key = (retried && !a.ident) ? null : resolveKey(a);
      var body = { quantity: a.quantity };
      if (key) body.id = key;
      else if (a.ident) return Promise.resolve(state.cart);   // 行已不存在(已被删/并入):目标已达成或无从修改,交给渲染对账
      else if (a.index > 0 && (retried || !a.key)) body.line = a.index;   // 身份未知的重试:回读后按最新序号兜底
      else if (a.key) body.id = a.key;
      else return Promise.resolve(state.cart);
      body.sections = DRAWER_SECTION;
      body.sections_url = root() + 'cart';
      return postJSON('cart/change.js', body).then(keepDrawerHtml).catch(function (e) {
        // 400/404:多半是 key 过期(折扣变化改了 key)。回读最新 cart 后,用最终目标值重试一次;422(库存等)不重试
        if (!retried && (e.status === 400 || e.status === 404)) {
          return readCart().then(function () { return attempt(true); });
        }
        throw e;
      });
    }
    return attempt(false).then(function (cart) {
      accept(cart, nextSeq());
      if (!a.removeSameGroup || !a.groupId) return cart;
      // 与 vendor updateItemQty 一致:同 _group_id 的其它行同步到同数量
      function next(c) {
        var other = (c.items || []).filter(function (it) {
          return it.properties && it.properties._group_id == a.groupId && it.quantity != a.quantity;
        })[0];
        if (!other) return c;
        return postJSON('cart/change.js', { id: other.key, quantity: a.quantity, sections: DRAWER_SECTION, sections_url: root() + 'cart' }).then(keepDrawerHtml).then(function (c2) {
          accept(c2, nextSeq()); return next(c2);
        });
      }
      return next(cart);
    });
  }

  /* ---------------- 串行队列 ---------------- */
  var queue = [];
  var running = false;

  function defer() {
    var d = {}; d.promise = new Promise(function (res, rej) { d.resolve = res; d.reject = rej; }); return d;
  }
  function enqueue(task) {
    if (task.type !== 'refresh' || task.open !== false) clearVisibleError();
    if (task.type !== 'refresh' || task.open !== false) noteVisibility(true);
    if (task.type !== 'refresh') clearCache();   // 任何 mutation:先作废 vendor 缓存,后续页面加载不会灌入旧抽屉
    // refresh 合并:队列里已有未开始的 refresh,直接复用
    if (task.type === 'refresh') {
      var pending = queue.filter(function (q) { return q.type === 'refresh'; })[0];
      if (pending) {
        if (task.open === false && pending.open !== false) { /* 保持 open */ }
        if (task.open !== false) pending.open = true;
        pending.sources.push(task.source);
        return pending.d.promise;
      }
      task.sources = [task.source]; task.d = defer(); queue.push(task);
    } else if (task.type === 'line') {
      // 同一行的数量修改合并为最后一个目标值(绝对数量,幂等)
      var same = queue.filter(function (q) {
        return q.type === 'line' && (
          (q.args.ident && q.args.ident === task.args.ident) ||
          (q.args.key && q.args.key === task.args.key) ||
          (!q.args.key && !task.args.key && q.args.index === task.args.index));
      })[0];
      if (same) { same.args.quantity = task.args.quantity; return same.d.promise; }
      task.d = defer(); queue.push(task);
    } else {
      task.d = defer(); queue.push(task);
    }
    drain();
    return task.d.promise;
  }

  function drain() {
    if (running) return;
    var focusBeforeBusy = captureUI().focus;
    running = true;
    setBusy(true);
    (async function () {
      try {
        while (queue.length) {
          var batch = [];
          var wantOpen = false;
          var sources = [];
          while (queue.length) {
            var tk = queue.shift();
            batch.push(tk);
            if (tk.type === 'refresh') { if (tk.open !== false) wantOpen = true; sources = sources.concat(tk.sources); }
            else { wantOpen = true; sources.push(tk.type); }
            ui.lineError = '';
            drawerHtml = null;
            if (tk.type === 'line') ui.msg = null;
            try { tk.result = await tk.run(); if (tk.type !== 'refresh') clearCache(); } catch (e) {
              tk.error = e;
              if (tk.type === 'line') {
                ui.lineError = (e && e.serverMessage) || t('update_error');
                announce(ui.lineError);
              }
            }
          }
          // The operation may already have failed before vendor reconciliation
          // finishes. Do not leave a previous success visible during that read.
          if (ui.msg && ui.msg.kind === 'error') applyMessage();
          var cart = await render(wantOpen, sources, focusBeforeBusy);
          focusBeforeBusy = null;
          batch.forEach(function (b) {
            if (b.type === 'refresh') b.d.resolve(cart);
            else if (b.error) b.d.resolve(b.type === 'line' ? cart : { status: 'error' });
            else b.d.resolve(b.result !== undefined ? b.result : cart);
          });
        }
      } finally {
        running = false;
        setBusy(false);
      }
    })();
  }

  /* ---------------- 渲染(只渲染一次) ---------------- */
  function renderOnce(open) {
    return new Promise(function (resolve, reject) {
      if (typeof $360 === 'undefined' || !$360 || typeof $360.showHeaderCart !== 'function' || !drawer()) { resolve(null); return; }
      var timer = setTimeout(function () { reject(new Error('render timeout')); }, RENDER_TIMEOUT);
      var ready = drawerHtml;
      drawerHtml = null;
      $360.showHeaderCart({ update: true, ndevFresh: true, ndevReady: ready }, function (n, error) {
        clearTimeout(timer);
        if (error) reject(error); else resolve(n);
      });
    });
  }
  function formSig() {
    var f = $('[data-ndev-cart-sig]');
    return f ? f.getAttribute('data-ndev-cart-sig') : null;
  }

  async function render(open, sources, focusBeforeBusy) {
    var wasOpen = !!(drawer() && drawer().classList.contains('active'));
    var passive = !open && sources && sources.length && sources.every(function (s) { return s === 'activity-window'; });
    // A background boundary read must not reopen a drawer closed while queued.
    if (passive && !wasOpen) return state.cart;
    var snap = captureUI();
    // Native disabled Apply loses focus before the asynchronous read completes.
    // Preserve its intent only while focus is still on BODY, never over a new control.
    if (!snap.focus && focusBeforeBusy && document.activeElement === document.body && wasOpen) snap.focus = focusBeforeBusy;
    // A failed native reprice must remain retryable; a fallback GET cannot
    // confirm the new pricing phase. Keep the existing controls/draft and show
    // the ordinary Cart error, without committing or verifying a stale read.
    if (activityRepriceKey) {
      var showReadError = !!open && !wasOpen && visibilityWantedOpen;
      ui.lineError = t('update_error');
      maskStale(false);
      if (!wasOpen && typeof $360 !== 'undefined' && $360 && typeof $360.hideHeaderCart === 'function') $360.hideHeaderCart();
      afterRender(state.cart, snap, sources, showReadError);
      return null;
    }
    var visibilityAtStart = visibilityEpoch;
    function closedSinceStart() { return passive && visibilityEpoch !== visibilityAtStart && !visibilityWantedOpen; }
    var n = null;
    // vendor 会在 fetch 前先打开抽屉:此时 DOM 若不是已对账的最新内容,先盖住并显示 loading
    var mask = !wasOpen || !domVerified();
    if (mask) maskStale(true);
    try {
      n = await renderOnce(open);
      // A cold drawer may only reveal its activity window in the first SSR
      // response. Reprice that window before removing the existing stale mask.
      var initialWindow = activityWindow();
      if (initialWindow && activityPricingConfirmedKey !== initialWindow.key) {
        requireActivityPricing(initialWindow);
        if (activityPricingConfirmedKey !== initialWindow.key) {
          await readCart();
          n = await renderOnce(open);
        }
      }
      var tries = 0;
      while (n && formSig() && formSig() !== sig(n) && tries < 2) {
        if (closedSinceStart()) break;
        tries++;
        n = await renderOnce(open);
      }
    } catch (e) {
      // Keep controls/draft on failure, but require a confirmed render before checkout.
      verifiedSig = null; // A failed fragment read must be retried even if totals did not change.
      ui.lineError = ui.lineError || t('update_error');
    }
    if (activityRepriceKey) n = null;
    if (n) { markVerified(n); accept(n, nextSeq()); }
    // 渲染失败且抽屉原本是关的:不露出可能过期的金额,收起(用户可重新打开重试);原本就开着则撤掉遮罩并显示错误提示
    var failedCold = mask && !n && !domVerified() && !wasOpen;
    var showColdError = failedCold && !!open && visibilityWantedOpen;
    if (failedCold && typeof $360 !== 'undefined' && $360 && typeof $360.hideHeaderCart === 'function') $360.hideHeaderCart();
    maskStale(false);
    if (!open && !wasOpen && typeof $360 !== 'undefined' && $360 && typeof $360.hideHeaderCart === 'function') $360.hideHeaderCart();
    if (closedSinceStart() && typeof $360 !== 'undefined' && typeof $360.hideHeaderCart === 'function') {
      $360.hideHeaderCart();
      snap.focus = null;
    }
    afterRender(n || state.cart, snap, sources, showColdError);
    return activityRepriceKey ? null : (n || state.cart);
  }

  function afterRender(cart, snap, sources, visibleError) {
    decorate(cart);
    applyMessage();
    restoreUI(snap);
    // 多个长码可将提交结果推到屏外；只响应本次用户提交的错误，
    // 保留焦点/草稿，不让普通刷新或移除操作改变阅读位置。
    if (ui.msg && ui.msg.text && sources && sources.indexOf('apply') !== -1) {
      var message = $('[data-ndev-promo-msg]');
      var scrollport = message && message.closest('form');
      if (message && !message.hidden && scrollport) {
        var messageBox = message.getBoundingClientRect();
        var viewportBox = scrollport.getBoundingClientRect();
        if (messageBox.top < viewportBox.top || messageBox.bottom > viewportBox.bottom) {
          message.scrollIntoView({ block: 'nearest', behavior: 'auto' });
        }
      }
    }
    var silent = sources && sources.length && sources.every(function (s) { return s === 'activity-window'; }) && drawer() && !drawer().classList.contains('active');
    if (!silent) {
      if (visibleError && ui.lineError) announce(ui.lineError, true);
      else if (ui.msg && ui.msg.text) announce(ui.msg.text + (ui.msg.ssHint && ssApplied() ? ' ' + t('promo_ss_better') : ''));
      else if (ui.codeNoticeText) announce(ui.codeNoticeText);
      else if (!ui.lineError) announce(t('promo_updated'));
    }
    try {
      document.dispatchEvent(new CustomEvent('ndev:cart:changed', { detail: { cart: cart, source: (sources && sources[0]) || 'ndevCart', sources: sources || [] } }));
    } catch (e) {}
  }

  /* ---------------- 公共 API ---------------- */
  var api = {
    version: '2026-10-16',
    refresh: function (opts) {
      opts = opts || {};
      // 刚写完购物车的刷新不再单独读一次 cart.js,渲染时会并行读取并确认。
      var afterWrite = (opts.source === 'legacy-initial' || opts.source === 'bundle') && Date.now() - latestCartWrite() < 5000;
      return enqueue({ type: 'refresh', source: opts.source || 'manual', open: opts.open, run: function () { return afterWrite && !activityRepriceKey ? Promise.resolve(state.cart) : readCart(); } });
    },
    applyCode: function (code) {
      return enqueue({ type: 'apply', run: function () { return doApply(code); } });
    },
    removeCode: function (code) {
      return enqueue({ type: 'remove', run: function () { return doRemove(code); } });
    },
    changeLine: function (a, qty) {
      var args;
      if (a && typeof a === 'object') {
        args = { key: a.key || null, index: a.index, quantity: a.quantity != null ? a.quantity : a.qty, groupId: a.groupId, removeSameGroup: !!a.removeSameGroup };
      } else if (typeof a === 'string' && !/^\d+$/.test(a)) {
        args = { key: a, quantity: qty };
      } else {
        args = { key: null, index: Number(a), quantity: qty };
      }
      args.quantity = Math.max(0, parseInt(args.quantity, 10));
      if (isNaN(args.quantity) || (!args.key && !(args.index > 0))) return Promise.resolve(state.cart);
      args.ident = identFromArgs(args);
      showLineChange(args);
      var task = { type: 'line', args: args };
      task.run = function () { return doChange(task.args); };
      return enqueue(task);
    },
    _afterLegacyRender: function (n) { if (n) { markVerified(n); accept(n, nextSeq()); decorate(n); applyMessage(); } },
    _state: state,
    _accept: accept,
    _nextSeq: nextSeq
  };
  window.ndevCart = api;

  /* Activity time boundaries: no continuous polling and no background drawer
   * opening. Liquid remains the first-paint authority; an already-open drawer
   * refreshes when the configured window starts/ends or a suspended tab resumes. */
  var activityTimer = null;
  var activityPhase = '';
  // 成功的购物车写操作本身会触发平台重新定价(实测 2026-11-02),边界之后已有写操作就不必再发空 update。
  // 只认浏览器确认的 HTTP 200;拿不到状态(旧 Safari)或边界前后 60 秒内(客户端时钟可能有偏差)一律退回重新定价。
  var lastCartWrite = 0;
  var writeObserver = null;
  function noteWrites(list) {
    list.forEach(function (e) {
      if (e.responseStatus === 200 && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(e.name)) {
        lastCartWrite = Math.max(lastCartWrite, performance.timeOrigin + e.responseEnd);
      }
    });
  }
  try {
    writeObserver = new PerformanceObserver(function (l) { noteWrites(l.getEntries()); });
    writeObserver.observe({ type: 'resource', buffered: true });
  } catch (e) { writeObserver = null; }
  // 购物车写请求统一从 fetch/XHR 的响应确认(Safari 的 Resource Timing 没有 responseStatus,实测 2026-11-02)。
  // 加购的 JSON 请求顺带取回抽屉 HTML,加购一返回就能显示;cart.js 仍独立读取并对账。
  // 响应对象和其它请求都原样透传;任何价格相关的写入都会作废已取回的抽屉,退回读取。
  var CART_WRITE_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_URL = /\/cart\/add(\.js)?(\?|$)/;
  var writeGen = 0;
  var writesInFlight = 0;
  var addDrawer = null;
  function changesPricing(url, body) {
    if (!/\/cart\/update(\.js)?(\?|$)/.test(url) || typeof body !== 'string') return true;
    try {
      var keys = Object.keys(JSON.parse(body));
      return !(keys.length === 1 && keys[0] === 'attributes');   // 第三方只写购物车属性,不影响金额
    } catch (e) { return true; }
  }
  function beginWrite(url, body) {
    var pricing = changesPricing(url, body);
    if (pricing) { writeGen++; writesInFlight++; addDrawer = null; }
    return function end(status) {
      if (pricing) writesInFlight--;
      if (status === 200) lastCartWrite = Math.max(lastCartWrite, Date.now());
    };
  }
  function takeAddDrawer() {
    var kept = addDrawer;
    addDrawer = null;
    return kept && kept.gen === writeGen && !writesInFlight && Date.now() - kept.at < 5000 ? kept.html : null;
  }
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = input && input.url ? input.url : String(input);
      if (!CART_WRITE_URL.test(url)) return nativeFetch.apply(this, arguments);
      var args = arguments;
      var body = init && init.body;
      var alone = !writesInFlight;
      var end = beginWrite(url, body);
      var gen = writeGen;
      var bundled = false;
      if (alone && CART_ADD_URL.test(url) && typeof input === 'string' && typeof body === 'string') {
        try {
          var parsed = JSON.parse(body);
          if (parsed && typeof parsed === 'object' && !parsed.sections) {
            parsed.sections = DRAWER_SECTION;
            parsed.sections_url = root() + 'cart';
            args = [input, Object.assign({}, init, { body: JSON.stringify(parsed) })];
            bundled = true;
          }
        } catch (e) {}
      }
      return nativeFetch.apply(this, args).then(function (r) {
        end(r.status);
        if (!bundled || r.status !== 200) return r;
        return r.clone().json().then(function (d) {
          var html = d && d.sections && d.sections[DRAWER_SECTION];
          if (html && gen === writeGen) addDrawer = { html: html, gen: gen, at: Date.now() };
        }).catch(function () {}).then(function () { return r; });
      }, function (e) { end(0); throw e; });
    };
  }
  var nativeXhrOpen = XMLHttpRequest.prototype.open;
  var nativeXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__ndevCartUrl = String(url);
    return nativeXhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (this.__ndevCartUrl && CART_WRITE_URL.test(this.__ndevCartUrl)) {
      var end = beginWrite(this.__ndevCartUrl, body);
      var xhr = this;
      this.addEventListener('loadend', function () { end(xhr.status); });
    }
    return nativeXhrSend.apply(this, arguments);
  };
  function latestCartWrite() {
    if (writeObserver) noteWrites(writeObserver.takeRecords());
    return lastCartWrite;
  }
  function writtenAfterBoundary(current) {
    var boundary = current.phase === 'active' ? current.start : (current.phase === 'ended' ? current.end : 0);
    return latestCartWrite() > boundary + 60000;
  }
  function requireActivityPricing(current) {
    if (current && activityPricingConfirmedKey !== current.key && activityRepriceKey !== current.key) {
      if (writtenAfterBoundary(current)) { activityPricingConfirmedKey = current.key; return; }
      activityRepriceKey = current.key;
      pricingEpoch++;
      clearCache();
      verifiedSig = null;
    }
  }
  function activityWindow() {
    var form = $('[data-ndev-activity-start][data-ndev-activity-end]');
    if (!form) return null;
    var start = Number(form.getAttribute('data-ndev-activity-start')) * 1000;
    var end = Number(form.getAttribute('data-ndev-activity-end')) * 1000;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    var now = Date.now();
    var phase = now < start ? 'before' : (now < end ? 'active' : 'ended');
    return { key: start + ':' + end + ':' + phase, phase: phase, start: start, end: end, next: now < start ? start : (now < end ? end : 0) };
  }
  function armActivityWindow() {
    clearTimeout(activityTimer);
    var current = activityWindow();
    activityPhase = current ? current.key : '';
    if (current && current.phase !== 'active') {
      var expiredCard = $('[data-ndev-ss]');
      if (expiredCard) expiredCard.remove();
    } else if (current && !activityRepriceKey && activityPricingConfirmedKey === current.key && domVerified() && !$('[data-ndev-ss]')) {
      var carrier = $('template[data-ndev-ss-template]');
      var pendingCard = carrier && carrier.content && carrier.content.querySelector('[data-ndev-ss]');
      if (pendingCard) carrier.replaceWith(pendingCard.cloneNode(true));
    }
    if (current && current.next) {
      activityTimer = setTimeout(checkActivityWindow, Math.min(2147483000, Math.max(1, current.next - Date.now())));
    }
  }
  function checkActivityWindow() {
    var current = activityWindow();
    var changed = current && activityPhase && current.key !== activityPhase;
    if (changed) {
      requireActivityPricing(current);
      clearCache();
      verifiedSig = null; // Same totals can still carry an expired activity card.
      if (current.phase !== 'active') {
        var card = $('[data-ndev-ss]');
        if (card) card.remove();
      }
    }
    armActivityWindow();
    if (changed && drawer() && drawer().classList.contains('active')) {
      api.refresh({source: 'activity-window', open: false}).catch(function () {});
    }
  }
  document.addEventListener('ndev:cart:changed', checkActivityWindow);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) checkActivityWindow(); });
  window.addEventListener('focus', checkActivityWindow);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', armActivityWindow, {once: true});
  else armActivityWindow();

  /* ---------------- 事件委托(抽屉整体替换后仍有效) ---------------- */
  var checkoutWaiting = false;
  document.addEventListener('click', function (e) {
    var checkout = e.target.closest && e.target.closest('#header-cart .checkout-btn');
    if (checkout) checkActivityWindow();
    if (checkout && (activityRepriceKey || !domVerified())) {
      e.preventDefault();
      e.stopImmediatePropagation();
      // Retry through the same queue, then let the customer review the confirmed
      // total before making a separate checkout action.
      api.refresh({ source: 'pricing-retry', open: true });
      return;
    }
    if (!checkout || !checkout.closest('[data-ndev-drawer]') || e.defaultPrevented) return;
    if (checkoutWaiting) { e.preventDefault(); return; }
    var waits = [];
    var accepting = true;
    var before = new CustomEvent('ndev:cart:before-checkout', {
      cancelable: true,
      detail: { waitFor: function (promise) {
        if (accepting) waits.push(Promise.resolve(promise));
      } }
    });
    document.dispatchEvent(before);
    accepting = false;
    // The default North American path has no listeners and keeps native navigation.
    if (!waits.length) return;
    e.preventDefault();
    checkoutWaiting = true;
    var timer;
    var timeout = new Promise(function (resolve) { timer = setTimeout(resolve, 3000); });
    Promise.race([Promise.allSettled(waits), timeout]).then(function () {
      clearTimeout(timer);
      checkoutWaiting = false;
      if (!before.defaultPrevented) window.location.href = '/checkout';
    });
  }, true);
  document.addEventListener('click', function (e) {
    var ap = e.target.closest && e.target.closest('[data-ndev-promo-apply]');
    if (ap) { e.preventDefault(); var inp = $('[data-ndev-promo-input]'); api.applyCode(inp ? inp.value : ''); return; }
    var rm = e.target.closest && e.target.closest('[data-ndev-remove-code]');
    if (rm) { e.preventDefault(); api.removeCode(rm.getAttribute('data-ndev-remove-code')); }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches('[data-ndev-promo-input]')) {
      e.preventDefault();
      api.applyCode(e.target.value);
    }
  });
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-ndev-promo-input]') && ui.msg) {
      // 重新输入即视为新草稿:清除旧错误提示,不改服务端
      setMsg('error', ''); applyMessage();
    }
  });

  // URL 带码(/discount/CODE 或 ?discount=):清除抽屉缓存,下次打开会按服务端当前 cart 重新取
  try {
    if (/[?&]discount=/.test(location.search) || /^\/discount\//.test(location.pathname)) sessionStorage.removeItem('shopify-header-cart');
  } catch (e) {}

  /* ---------------- $360.showHeaderCart 运行时去重包装 ----------------
   * 背景:vendor `updateItemQty` 末尾是逗号表达式 `r && …, o(i, l), o(i, l)`,回调被调用两次,
   * 每次回调都 `$360.showHeaderCart({update:true})` → 抽屉连续请求/渲染两次;另有 44+27 个旧入口各自调用。
   * 做法:只包装 update:true 的刷新。在途时到达的重复调用并入同一次请求(回调全部在完成时调用一次);
   *   · 非 update 调用(仅打开抽屉)原样透传,保留同步行为与返回值;
   *   · ndevCart 自己的渲染带 ndevFresh:true,在途时不并入而是排在其后(它刚完成写操作,不能拿更早开始的读);
   *   · 非同一 tick 并入的调用视为「迟到」,完成后用 cart.js 的 sig 对账,不一致再补渲染一次。
   * `$360` 是 vendor 里的 const 词法绑定(不在 window 上),这里直接按标识符访问。
   * vendor 的可选提交门会在计数和 DOM 更新前丢弃失效响应；未传提交门的旧版入口保持原行为。
   * 原签名 showHeaderCart(opts, callback) 与返回值(undefined)不变。 */
  /* 抽屉渲染:cart.js 与抽屉 HTML 并行读取,替代 vendor 的 cart.js → cart?view=json 串行读取,少一次往返。
   * 后处理与 vendor showHeaderCart 的 update 分支保持同序同结果;只读不下单、不写购物车。
   * section 不可用(404/返回 null)时退回 vendor 原链路,不影响金额。 */
  function renderDrawer(self, orig, o, finish) {
    var canCommit = o.ndevCanCommit;
    var headerCart = drawer();
    if (!headerCart) return orig.call(self, o, finish);
    headerCart.classList.add('active');
    var ready = o.ndevReady;
    delete o.ndevReady;
    function crossSell(count) {
      var names = ['flex', 'column-reverse', 'jc-between', 't-flex-row', 't-jc-start'];
      if (count > 0 && headerCart.querySelector('.cross-sell')) {
        headerCart.classList.remove('no-cross-sell');
        names.forEach(function (c) { headerCart.classList.add(c); });
      } else {
        headerCart.classList.add('no-cross-sell');
        names.forEach(function (c) { headerCart.classList.remove(c); });
      }
    }
    function parse(html) {
      var box = document.createElement('div');
      box.innerHTML = html;
      return box.firstElementChild;
    }
    function paint(wrap) {
      var markup = wrap ? wrap.innerHTML : '';
      if (!markup) return false;
      headerCart.innerHTML = '';
      try { sessionStorage.setItem('shopify-header-cart', markup); } catch (e) {}
      headerCart.append(document.createRange().createContextualFragment(markup));
      self.adjustCartHeight();
      orig.call(self, {});
      return true;
    }
    // 活动边界 60 秒内不提前显示:这份 HTML 可能要等重新定价后才算数。
    function safeToShowEarly(wrap) {
      var form = wrap && wrap.querySelector('[data-ndev-activity-start][data-ndev-activity-end]');
      if (!form) return true;
      var start = Number(form.getAttribute('data-ndev-activity-start')) * 1000;
      var end = Number(form.getAttribute('data-ndev-activity-end')) * 1000;
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
      var now = Date.now();
      var boundary = now < start ? 0 : (now < end ? start : end);
      return lastCartWrite > boundary + 60000 && (!(now < start) || now + 60000 < start) && (!(now < end) || now + 60000 < end);
    }
    var early = !ready && !activityRepriceKey ? takeAddDrawer() : null;
    var painted = false;
    if (early && canCommit()) {
      var earlyWrap = parse(early);
      if (safeToShowEarly(earlyWrap) && paint(earlyWrap)) {
        painted = true;
        crossSell(parseInt(early.match(/data-ndev-cart-sig="(\d+)/) ? early.match(/data-ndev-cart-sig="(\d+)/)[1] : '0', 10));
        maskStale(false);
      }
    }
    var reads = ready ? [ready.cart, ready.html] : [
      request('cart.js?_=' + Date.now()),
      early || fetch(root() + 'cart?sections=' + DRAWER_SECTION, { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) { return data && data[DRAWER_SECTION]; }, function () { return null; })
    ];
    return Promise.all(reads).then(function (results) {
      var n = results[0], html = results[1];
      if (!canCommit()) { finish(n); return; }
      if (!painted) {
        if (!html) { orig.call(self, o, finish); return; }
        var wrap = parse(html);
        if (!wrap || !wrap.innerHTML) { orig.call(self, o, finish); return; }
        self.updateHeaderCartCount(n);
        paint(wrap);
      } else {
        self.updateHeaderCartCount(n);
      }
      crossSell(n.item_count);
      finish(n);
      if (window.KlarnaOnsiteService) window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
      setTimeout(function () { self.adjustCartHeight(); }, 1000);
    }).catch(function (e) {
      finish(null, canCommit() ? e : null);
    });
  }
  function installDedupe() {
    if (typeof $360 === 'undefined' || !$360 || typeof $360.showHeaderCart !== 'function') return false;
    if ($360.showHeaderCart.__ndevDedupe) return true;
    var orig = $360.showHeaderCart;
    var originalHide = $360.hideHeaderCart;
    var closedUI = null;
    var inflight = null;       // { waiters:[], late:bool, trailing:[] , timer }
    var tickOpen = false;

    function start(self, opts, waiters) {
      var job = { waiters: waiters, late: false, trailing: [], timer: null, done: false, snapshot: null, pricingEpoch: pricingEpoch };
      inflight = job;
      tickOpen = true;
      setTimeout(function () { tickOpen = false; }, 0);
      function finish(n, renderError) {
        // Vendor can finish after our render/job timeouts and reopen the drawer
        // before this callback. The latest explicit intent still takes priority.
        if (!visibilityWantedOpen && typeof originalHide === 'function') {
          originalHide.call(self);
          restoreUI(closedUI);
        }
        if (job.done) return;
        if (job.pricingEpoch !== pricingEpoch || activityRepriceKey) n = null;
        job.done = true;
        clearTimeout(job.timer);
        function release() {
          var ownsRead = inflight === job;
          if (ownsRead) inflight = null;
          if (!ownsRead || job.pricingEpoch !== pricingEpoch || activityRepriceKey) n = null;
          if (n) { try { api._afterLegacyRender(n); } catch (e) {} }
          // External legacy callers do not pass through render()/afterRender().
          // Preserve their latest draft without consuming an owned queue's
          // clearDraft flag or restoring focus into a closed drawer.
          if (n && !running && job.snapshot) {
            if (!visibilityWantedOpen) job.snapshot.focus = null;
            restoreUI(job.snapshot);
          }
          job.waiters.forEach(function (cb) { try { cb(n, renderError); } catch (e) { setTimeout(function () { throw e; }); } });
          if (job.trailing.length) {
            if (inflight && !inflight.done) Array.prototype.push.apply(inflight.trailing, job.trailing);
            else start(self, { update: true }, job.trailing);
          }
        }
        if (job.late && n) {
          // 迟到并入的调用:确认渲染所用 cart 仍是最新
          request('cart.js?_=' + Date.now()).then(function (c) {
            if (sig(c) !== sig(n)) { inflight = null; start(self, { update: true }, job.waiters.concat(job.trailing)); }
            else release();
          }, release);
        } else release();
      }
      job.timer = setTimeout(function () { job.done = true; inflight = null; if (job.trailing.length) start(self, { update: true }, job.trailing); }, RENDER_TIMEOUT + 3000);
      var o = {};
      Object.keys(opts || {}).forEach(function (k) { if (k !== 'ndevFresh') o[k] = opts[k]; });
      o.ndevCanCommit = function () {
        checkActivityWindow();
        var current = !job.done && inflight === job;
        var pricingCurrent = job.pricingEpoch === pricingEpoch && !activityRepriceKey;
        if (current && pricingCurrent && !running) job.snapshot = captureUI();
        return current && pricingCurrent;
      };
      return renderDrawer(self, orig, o, finish);
    }

    var wrapped = function (opts, cb) {
      var update = !!(opts && typeof opts === 'object' && opts.update);
      if (!update) {
        noteVisibility(true);
        checkActivityWindow();
        // 仅已对账的DOM可直接打开。空抽屉也走统一渲染生命周期：
        // vendor首次打开本身不派发changed事件，否则活动起止定时器不会注册。
        if (!domVerified()) {
          var cbOpen = typeof cb === 'function' ? cb : null;
          api.refresh({ source: 'open', open: true }).then(function (c) { if (cbOpen) cbOpen(c); });
          return;
        }
        return orig.apply(this, arguments);
      }
      var fresh = !!(opts && opts.ndevFresh);
      if (!fresh) noteVisibility(true);
      // Legacy add/refresh can be the first Cart entry on this document too.
      // Use the owned cold-render path so a newly discovered window is priced
      // before its first visible frame and the callback receives final Cart.
      if (!fresh && (!state.cart || activityRepriceKey)) {
        api.refresh({ source: 'legacy-initial', open: true }).then(function (c) { if (typeof cb === 'function') cb(c); });
        return;
      }
      if (inflight && !inflight.done) {
        if (fresh) { if (cb) inflight.trailing.push(cb); else inflight.trailing.push(function () {}); return; }
        if (!tickOpen) inflight.late = true;
        if (typeof cb === 'function') inflight.waiters.push(cb);
        return;
      }
      return start(this, opts, typeof cb === 'function' ? [cb] : []);
    };
    wrapped.__ndevDedupe = true;
    wrapped.__orig = orig;
    $360.showHeaderCart = wrapped;
    if (typeof $360.hideHeaderCart === 'function') {
      $360.hideHeaderCart = function () {
        closedUI = captureUI();
        closedUI.focus = null;
        noteVisibility(false);
        return originalHide.apply(this, arguments);
      };
    }
    return true;
  }
  if (!installDedupe()) {
    // vendor 尚未执行(理论上 theme.liquid 把本脚本放在 vendor 之后):DOMContentLoaded 再试一次
    document.addEventListener('DOMContentLoaded', installDedupe);
  }
})();
